/**
 * Unified API Service for MDS Queue
 *
 * Consolidates workItemsApi.ts and widget API functionality into a single service
 * with optional authentication support for both main app and widget contexts.
 */

import type {
  WorkItem,
  Nurse,
  Summary,
  AssignRequest,
  AcceptRequest,
  RejectRequest,
  StartAssessmentRequest,
  CompleteRequest,
  ActionResponse,
  StartAssessmentResponse,
  WorkItemsQueryParams,
  SummaryQueryParams,
  AssessmentQueryParams,
  AssessmentsResponse,
  MdsAssessmentsResponse,
  CareAssessmentsResponse,
} from "../types/workItems";

import type {
  ResidentSummaryListResponse,
  GetResidentsResponse,
  RiskFactorSummaryResponse,
  CareQueueNotesResponse,
  ProgressNotesResponse,
  VitalSignsResponse,
  MedicationsResponse,
  InfectionsResponse,
  ResidentOrders,
  FallIncidentsResponse,
  RiskTrendResponse,
  CreateCareQueueNoteRequest,
  CreateCareQueueNoteResponse,
  UnifiedReviewRequest,
  UnifiedReviewResponse,
  HideResidentRequest,
  HideResidentResponse,
  AiFeedbackCreateRequest,
  AiFeedbackCreateResponse,
  AiFeedbackListResponse,
  AiFeedbackContentType,
  FunctionalAbilitiesResponse,
} from "../types/residents";

import { mapRiskDataToSummary } from "../types/residents";

import type {
  CareQueueConfigApiResponse,
  SaveCareQueueConfigApiRequest,
  FacilityRiskConfigApiResponse,
} from "../types/configuration";

import type { FacilityUiDefaults } from "../types/dashboard";

// Response type for filter options API
export interface FilterOptionsResponse {
  statuses: string[];
  payers: string[];
  assessment_types: string[];
  stations: string[];
  buildings: string[];
  assigned_owners: string[];
}

// CareQueue permission flags returned alongside user info
export interface CareQueuePermissions {
  CanDismissWorkItems: boolean;
  CanScheduleJob: boolean;
  CanConfigureRiskPolicy: boolean;
  CanViewJobStatus: boolean;
  CanViewResidentTab: boolean;
  CanViewMDSTab: boolean;
  CanViewCareAssessmentTab: boolean;
  // NETSOL-11790: when true, the Care Queue is restricted to only the
  // residents assigned to the current user (toggle forced on and locked).
  RestrictToAssignedResidents: boolean;
  // NETSOL-11843: true when the user's facility is licensed for My Assignments
  // (Care Assignments product). Gates the toggle's visibility — the toggle is
  // only shown when licensed.
  MyAssignmentsLicensed: boolean;
}

// Response type for current user info API
export interface UserInfoResponse {
  userSnbr: number;
  userID: string;
  userName: string;
  userTitle: string;
  userInitials: string;
  userStatus: string;
  CareQueuePermissions?: CareQueuePermissions;
  // Token claims
  userId?: string;
  clientId?: string;
  facilityId?: string;
  /** Comma-separated list of all facility IDs the user can access */
  facilityIds?: string;
  admin?: boolean;
}

// API Configuration interface
export interface ApiConfig {
  baseUrl: string;
  auth?: {
    token?: string;
    apiKey?: string;
    ehrToken?: string;
    clientId?: string;
    facilityId?: string;
    customHeaders?: Record<string, string>;
    tokenProvider?: () => Promise<string>;
  };
  onError?: (error: Error) => void;
}

// Helper function to build query string
function buildQueryString(
  params: WorkItemsQueryParams | SummaryQueryParams | AssessmentQueryParams
): string {
  const filteredParams = Object.entries(params)
    .filter(([, value]) => value !== undefined)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value!))}`
    )
    .join("&");
  return filteredParams ? `?${filteredParams}` : "";
}

/**
 * Creates an authenticated fetch function with the provided configuration
 */
function createApiClient(config: ApiConfig) {
  return async function apiFetch(
    endpoint: string,
    options?: RequestInit
  ): Promise<Response> {
    const headers = new Headers(options?.headers);

    // Set default content type for JSON requests with body
    if (!headers.has("Content-Type") && options?.body) {
      headers.set("Content-Type", "application/json");
    }

    // Apply authentication if configured
    if (config.auth?.token) {
      // Bearer Token authentication
      headers.set("Authorization", `Bearer ${config.auth.token}`);
    } else if (config.auth?.tokenProvider) {
      // Dynamic token provider
      try {
        const token = await config.auth.tokenProvider();
        headers.set("Authorization", `Bearer ${token}`);
      } catch (error) {
        console.error("[API Service] Token provider failed:", error);
        throw new Error("Failed to obtain authentication token");
      }
    } else if (config.auth?.apiKey) {
      // API Key authentication
      headers.set("X-API-Key", config.auth.apiKey);
    }

    // Apply custom authentication headers
    if (config.auth?.ehrToken) {
      headers.set("ehrToken", config.auth.ehrToken);
    }
    if (config.auth?.clientId) {
      headers.set("x-client-id", config.auth.clientId);
    }
    if (config.auth?.facilityId) {
      headers.set("x-facility-id", config.auth.facilityId);
    }
    if (config.auth?.token) {
      headers.set("token", config.auth.token);
    }

    // Apply custom headers
    if (config.auth?.customHeaders) {
      Object.entries(config.auth.customHeaders).forEach(([key, value]) => {
        headers.set(key, value);
      });
    }

    // Build full URL
    const url = `${config.baseUrl}${endpoint}`;

    // Make request
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle authentication errors
    if (response.status === 401) {
      const error = new Error("Authentication failed - invalid or expired token");
      config.onError?.(error);
      throw error;
    }

    if (response.status === 403) {
      const error = new Error("Authorization failed - insufficient permissions");
      config.onError?.(error);
      throw error;
    }

    return response;
  };
}

/**
 * Creates API service with the provided configuration
 */
export function createApiService(config: ApiConfig) {
  const apiFetch = createApiClient(config);

  return {
    // GET /api/mds-assessments - Get MDS assessments
    getMdsAssessments: async (
      params?: AssessmentQueryParams,
      signal?: AbortSignal
    ): Promise<MdsAssessmentsResponse> => {
      const queryString = params ? buildQueryString(params) : "";
      const response = await apiFetch(`/api/mds-assessments${queryString}`, {
        signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch MDS assessments: ${response.statusText}`);
      }

      return response.json();
    },

    // GET /api/care-assessments - Get care assessments
    getCareAssessments: async (
      params?: AssessmentQueryParams,
      signal?: AbortSignal
    ): Promise<CareAssessmentsResponse> => {
      const queryString = params ? buildQueryString(params) : "";
      const response = await apiFetch(`/api/care-assessments${queryString}`, {
        signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch care assessments: ${response.statusText}`);
      }

      return response.json();
    },

    // GET /api/work-items - Get all work items with optional filters
    getWorkItems: async (
      params?: WorkItemsQueryParams
    ): Promise<AssessmentsResponse> => {
      const queryString = params ? buildQueryString(params) : "";
      const response = await apiFetch(`/api/work-items${queryString}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch work items: ${response.statusText}`);
      }

      return response.json();
    },

    // GET /api/work-items/{work_item_id} - Get specific work item
    getSingleWorkItem: async (workItemId: string, signal?: AbortSignal): Promise<WorkItem> => {
      const response = await apiFetch(`/api/work-items/${workItemId}`, {
        signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch work item: ${response.statusText}`);
      }

      const data = await response.json();
      // API returns { work_item: {...} }, so extract the work_item
      return data.work_item || data;
    },

    // Alias for getSingleWorkItem to match widget API
    getWorkItem: async (workItemId: string): Promise<WorkItem> => {
      const response = await apiFetch(`/api/work-items/${workItemId}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch work item: ${response.statusText}`);
      }

      const data = await response.json();
      // API returns { work_item: {...} }, so extract the work_item
      return data.work_item || data;
    },

    // PATCH /api/work-items/{work_item_id} - Update work item
    updateWorkItem: async (
      workItemId: string,
      updates: Record<string, unknown>
    ): Promise<WorkItem> => {
      const response = await apiFetch(`/api/work-items/${workItemId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error(`Failed to update work item: ${response.statusText}`);
      }

      return response.json();
    },

    // GET /api/summary - Get summary statistics
    getSummary: async (params?: SummaryQueryParams): Promise<Summary> => {
      const queryString = params ? buildQueryString(params) : "";
      const response = await apiFetch(`/api/summary${queryString}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch summary: ${response.statusText}`);
      }

      return response.json();
    },

    // GET /api/nurses - Get all nurses
    getNurses: async (): Promise<Record<string, Nurse[]>> => {
      const response = await apiFetch(`/api/nurses`);

      if (!response.ok) {
        throw new Error(`Failed to fetch nurses: ${response.statusText}`);
      }

      return response.json();
    },

    // POST /api/work-items/{work_item_id}/assign - Assign work item to a nurse
    assignWorkItem: async (
      workItemId: string,
      request: AssignRequest
    ): Promise<ActionResponse> => {
      const response = await apiFetch(`/api/work-items/${workItemId}/assign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Failed to assign work item: ${response.statusText}`);
      }

      return response.json();
    },

    // POST /api/work-items/{work_item_id}/accept - Nurse accepts assigned work item
    acceptWorkItem: async (
      workItemId: string,
      request: AcceptRequest
    ): Promise<ActionResponse> => {
      const response = await apiFetch(`/api/work-items/${workItemId}/accept`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Failed to accept work item: ${response.statusText}`);
      }

      return response.json();
    },

    // POST /api/work-items/{work_item_id}/start-assessment - Start assessment (launches EHR)
    startAssessment: async (
      workItemId: string,
      request: StartAssessmentRequest
    ): Promise<StartAssessmentResponse> => {
      const response = await apiFetch(
        `/api/work-items/${workItemId}/start-assessment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to start assessment: ${response.statusText}`);
      }

      return response.json();
    },

    // POST /api/work-items/{work_item_id}/reject - Nurse rejects work item
    rejectWorkItem: async (
      workItemId: string,
      request: RejectRequest
    ): Promise<ActionResponse> => {
      const response = await apiFetch(`/api/work-items/${workItemId}/reject`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Failed to reject work item: ${response.statusText}`);
      }

      return response.json();
    },

    // POST /api/work-items/{work_item_id}/complete - Manually complete work item
    completeWorkItem: async (
      workItemId: string,
      request: CompleteRequest
    ): Promise<ActionResponse> => {
      const response = await apiFetch(`/api/work-items/${workItemId}/complete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Failed to complete work item: ${response.statusText}`);
      }

      return response.json();
    },

    // GET /api/work-items/{work_item_id}/history - Get work item history (audit trail)
    getWorkItemHistory: async (workItemId: string): Promise<unknown> => {
      const response = await apiFetch(`/api/work-items/${workItemId}/history`);

      if (!response.ok) {
        throw new Error(`Failed to fetch work item history: ${response.statusText}`);
      }

      return response.json();
    },

    // POST /api/work-items/{work_item_id}/dismiss - Dismiss work item
    dismissWorkItem: async (
      workItemId: string,
      reason?: string
    ): Promise<ActionResponse> => {
      const response = await apiFetch(`/api/work-items/${workItemId}/dismiss`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ reason: reason || null }),
      });

      if (!response.ok) {
        throw new Error(`Failed to dismiss work item: ${response.statusText}`);
      }

      return response.json();
    },

    // GET /api/filter-options - Get dynamic filter options
    getFilterOptions: async (): Promise<FilterOptionsResponse> => {
      const response = await apiFetch(`/api/filter-options`);

      if (!response.ok) {
        throw new Error(`Failed to fetch filter options: ${response.statusText}`);
      }

      return response.json();
    },

    // GET /api/auth/me - Get identity from token (no EHR calls, instant)
    getCurrentUser: async (): Promise<UserInfoResponse> => {
      const response = await apiFetch(`/api/auth/me`);

      if (!response.ok) {
        throw new Error(`Failed to fetch current user: ${response.statusText}`);
      }

      return response.json();
    },

    // GET /api/auth/permissions - Fetch CareQueue permissions from EHR
    getPermissions: async (): Promise<{ CareQueuePermissions: CareQueuePermissions }> => {
      const response = await apiFetch(`/api/auth/permissions`);

      if (!response.ok) {
        // NETSOL-11843: throw on non-OK rather than resolving with all-false
        // defaults. A default object is indistinguishable from a real "no
        // permissions" response, so a transient failure (e.g. a 401 during token
        // rotation) used to silently hide the My Assignments toggle and drop the
        // provider security restriction. The Dashboard's catch preserves the
        // last-known-good permissions on failure; only the very first load falls
        // back to the component's built-in defaults.
        throw new Error(`Failed to fetch permissions: ${response.statusText}`);
      }

      return response.json();
    },

    // Raw fetch function for advanced use cases
    fetch: apiFetch,

    // ========================================================================
    // Residents API Methods
    // ========================================================================

    // GET /api/residents - Get residents list (maps to ResidentSummaryListResponse)
    getResidentsSummary: async (
      showHidden?: boolean,
      signal?: AbortSignal,
      facilityIds?: string[],
      myAssignments?: boolean
    ): Promise<ResidentSummaryListResponse> => {
      const params = new URLSearchParams();
      if (showHidden) {
        params.set("show_hidden", "true");
      }
      if (facilityIds && facilityIds.length > 0) {
        facilityIds.forEach((id) => params.append("facility_id", id));
      }
      // NETSOL-11791: restrict to residents assigned to the current user.
      if (myAssignments) {
        params.set("my_assignments", "true");
      }
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(`/api/residents${queryString}`, {
        signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch residents summary: ${response.statusText}`);
      }

      const data: GetResidentsResponse = await response.json();

      return {
        residents: data.residents.map(mapRiskDataToSummary),
        count: data.total,
      };
    },

    // GET /api/residents/{residentId}/riskFactorSummary - Get risk factor summary
    getResidentRiskFactors: async (
      residentId: string,
      asOf?: string,
      signal?: AbortSignal,
      wqId?: string
    ): Promise<RiskFactorSummaryResponse> => {
      const params = new URLSearchParams();
      if (asOf) {
        params.set("asOf", asOf);
      }
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/riskFactorSummary${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch risk factors: ${response.statusText}`);
      }

      return response.json();
    },

    // GET /api/residents/{residentId}/carequeue_notes - Get care queue notes
    getResidentCareQueueNotes: async (
      residentId: string,
      asOf?: string,
      signal?: AbortSignal,
      wqId?: string
    ): Promise<CareQueueNotesResponse> => {
      const params = new URLSearchParams();
      if (asOf) {
        params.set("asOf", asOf);
      }
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/carequeue_notes${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch care queue notes: ${response.statusText}`);
      }

      return response.json();
    },

    // POST /api/residents/{residentId}/carequeue_notes - Create care queue note
    createCareQueueNote: async (
      residentId: string,
      request: CreateCareQueueNoteRequest,
      wqId?: string
    ): Promise<CreateCareQueueNoteResponse> => {
      const params = new URLSearchParams();
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/carequeue_notes${queryString}`,
        {
          method: "POST",
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to create care queue note: ${response.statusText}`);
      }

      return response.json();
    },

    // POST /api/residents/{residentId}/review-all - Mark resident as reviewed
    markResidentReviewed: async (
      residentId: string,
      request: UnifiedReviewRequest,
      wqId?: string
    ): Promise<UnifiedReviewResponse> => {
      const params = new URLSearchParams();
      const clientId = config.auth?.clientId;
      const facilityId = config.auth?.facilityId;
      if (clientId) params.set("client_id", clientId);
      if (facilityId) params.set("facility_id", facilityId);
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/review-all${queryString}`,
        {
          method: "POST",
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to mark resident as reviewed: ${response.statusText}`);
      }

      return response.json();
    },

    // POST /api/residents/{residentId}/hide - Hide resident for # days
    hideResident: async (
      residentId: string,
      request: HideResidentRequest,
      wqId?: string
    ): Promise<HideResidentResponse> => {
      const params = new URLSearchParams();
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/hide${queryString}`,
        {
          method: "POST",
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to hide resident: ${response.statusText}`);
      }

      return response.json();
    },

    // DELETE /api/residents/{residentId}/hide - Unhide resident
    unhideResident: async (residentId: string, wqId?: string): Promise<void> => {
      const params = new URLSearchParams();
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/hide${queryString}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to unhide resident: ${response.statusText}`);
      }
    },

    // GET /api/residents/{residentId}/progress/notes - Get progress notes
    getResidentProgressNotes: async (
      residentId: string,
      asOf?: string,
      window?: number,
      signal?: AbortSignal,
      wqId?: string
    ): Promise<ProgressNotesResponse> => {
      const params = new URLSearchParams();
      if (asOf) {
        params.set("asOf", asOf);
      }
      if (window !== undefined) {
        params.set("window", String(window));
      }
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/progress_notes${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch progress notes: ${response.statusText}`);
      }

      return response.json();
    },

    // GET /api/residents/{residentId}/metrics/vitalSigns - Get vital signs
    getResidentVitalSigns: async (
      residentId: string,
      asOf?: string,
      window?: number,
      signal?: AbortSignal,
      wqId?: string
    ): Promise<VitalSignsResponse> => {
      const params = new URLSearchParams();
      if (asOf) {
        params.set("asOf", asOf);
      }
      if (window !== undefined) {
        params.set("window", String(window));
      }
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/metrics/vitalSigns${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch vital signs: ${response.statusText}`);
      }

      return response.json();
    },

    // GET /api/residents/{residentId}/medications - Get medications
    getResidentMedications: async (
      residentId: string,
      asOf?: string,
      window?: number,
      newOnly: boolean = true,  // Deprecated parameter
      signal?: AbortSignal,
      wqId?: string
    ): Promise<MedicationsResponse> => {
      const params = new URLSearchParams();
      if (asOf) {
        params.set("asOf", asOf);
      }
      if (window) {
        params.set("window", window.toString());
      }
      params.set("newOnly", String(newOnly));
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/medications${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch medications: ${response.statusText}`);
      }

      return response.json();
    },

    // GET /api/residents/{residentId}/infections - Get infections
    getResidentInfections: async (
      residentId: string,
      asOf?: string,
      window?: number,
      signal?: AbortSignal,
      wqId?: string
    ): Promise<InfectionsResponse> => {
      const params = new URLSearchParams();
      if (asOf) {
        params.set("asOf", asOf);
      }
      if (window) {
        params.set("window", window.toString());
      }
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/infections${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch infections: ${response.statusText}`);
      }

      return response.json();
    },

    // GET /api/residents/{residentId}/orders - Get orders
    getResidentOrders: async (
      residentId: string,
      signal?: AbortSignal,
      wqId?: string
    ): Promise<ResidentOrders> => {
      const params = new URLSearchParams();
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/orders${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch orders: ${response.statusText}`);
      }

      return response.json();
    },

    // GET /api/residents/{residentId}/fall_incidents - Get fall incidents
    getResidentFallIncidents: async (
      residentId: string,
      window?: number,
      signal?: AbortSignal,
      wqId?: string
    ): Promise<FallIncidentsResponse> => {
      const params = new URLSearchParams();
      if (window) {
        params.set("window", window.toString());
      }
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/fall_incidents${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch fall incidents: ${response.statusText}`);
      }

      return response.json();
    },

    // GET /api/residents/summary/{patientId}/riskTrend - Get risk trend
    getResidentRiskTrend: async (
      patientId: string,
      signal?: AbortSignal
    ): Promise<RiskTrendResponse> => {
      const response = await apiFetch(
        `/api/residents/summary/${patientId}/riskTrend`,
        { signal }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch risk trend: ${response.statusText}`);
      }

      return response.json();
    },

    // GET /api/residents/{residentId}/functional_abilities
    getResidentFunctionalAbilities: async (
      residentId: string,
      signal?: AbortSignal,
      wqId?: string,
      days?: number
    ): Promise<FunctionalAbilitiesResponse> => {
      const params = new URLSearchParams();
      if (wqId) params.set("wq_id", wqId);
      if (days !== undefined) params.set("days", String(days));
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/functional_abilities${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch functional abilities: ${response.statusText}`);
      }

      return response.json();
    },

    // ========================================================================
    // Care Queue Configuration API Methods
    // ========================================================================

    // GET /api/v1/admin/care-queue/config - Get resident tab configuration
    getResidentTabConfiguration: async (
      signal?: AbortSignal
    ): Promise<CareQueueConfigApiResponse> => {
      const response = await apiFetch(`/api/admin/care-queue/config`, {
        signal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch configuration: ${response.statusText}`
        );
      }

      return response.json();
    },

    // PUT /api/v1/admin/care-queue/config - Save resident tab configuration
    saveResidentTabConfiguration: async (
      configPayload: SaveCareQueueConfigApiRequest
    ): Promise<CareQueueConfigApiResponse> => {
      const response = await apiFetch(`/api/admin/care-queue/config`, {
        method: "PUT",
        body: JSON.stringify(configPayload),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to save configuration: ${response.statusText}`
        );
      }

      return response.json();
    },

    // GET /api/facility-settings/risk-config - Get disabled risk factors per facility
    // Uses raw fetch (not apiFetch) so auth errors don't trigger the global onError callback.
    // A 403/401 here is non-fatal — the widget falls back to "all factors enabled".
    getRiskFactorConfig: async (
      signal?: AbortSignal
    ): Promise<FacilityRiskConfigApiResponse | null> => {
      try {
        const headers = new Headers();
        if (config.auth?.token) {
          headers.set("Authorization", `Bearer ${config.auth.token}`);
        } else if (config.auth?.tokenProvider) {
          const token = await config.auth.tokenProvider();
          headers.set("Authorization", `Bearer ${token}`);
        }
        if (config.auth?.clientId) headers.set("x-client-id", config.auth.clientId);
        if (config.auth?.facilityId) headers.set("x-facility-id", config.auth.facilityId);

        const response = await fetch(`${config.baseUrl}/api/facility-settings/risk-config`, {
          headers,
          signal,
        });
        if (!response.ok) return null;
        return response.json();
      } catch {
        return null; // treat errors as "all factors enabled"
      }
    },

    // ========================================================================
    // AI Feedback API Methods
    // ========================================================================

    // POST /api/residents/{residentId}/ai-feedback - Submit AI feedback
    submitAiFeedback: async (
      residentId: string,
      request: AiFeedbackCreateRequest,
      wqId?: string
    ): Promise<AiFeedbackCreateResponse> => {
      const params = new URLSearchParams();
      if (wqId) params.set("wq_id", wqId);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiFetch(
        `/api/residents/${residentId}/ai-feedback${queryString}`,
        {
          method: "POST",
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to submit AI feedback: ${response.statusText}`);
      }

      return response.json();
    },

    // GET /api/residents/{residentId}/ai-feedback - Get existing AI feedback
    getAiFeedback: async (
      residentId: string,
      riskDocumentId: string,
      contentType?: AiFeedbackContentType,
      wqId?: string,
      signal?: AbortSignal
    ): Promise<AiFeedbackListResponse> => {
      const params = new URLSearchParams();
      params.set("risk_document_id", riskDocumentId);
      if (contentType) params.set("content_type", contentType);
      if (wqId) params.set("wq_id", wqId);
      const queryString = `?${params.toString()}`;
      const response = await apiFetch(
        `/api/residents/${residentId}/ai-feedback${queryString}`,
        { signal }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch AI feedback: ${response.statusText}`);
      }

      return response.json();
    },

    // ========================================================================
    // Facility Settings API Methods
    // ========================================================================

    // GET /api/facility-settings/ui-defaults - Get UI defaults for all facilities
    // Returns Record<facilityId, FacilityUiDefaults> keyed by facility_id.
    // Uses raw fetch (not apiFetch) so auth errors don't trigger the global onError callback.
    // A 403/401 here is non-fatal — the widget falls back to Pydantic defaults per facility.
    getUiDefaults: async (
      signal?: AbortSignal
    ): Promise<Record<string, FacilityUiDefaults> | null> => {
      const headers = new Headers();
      if (config.auth?.token) {
        headers.set("Authorization", `Bearer ${config.auth.token}`);
      } else if (config.auth?.tokenProvider) {
        try {
          const token = await config.auth.tokenProvider();
          headers.set("Authorization", `Bearer ${token}`);
        } catch {
          return null;
        }
      }
      if (config.auth?.clientId) headers.set("x-client-id", config.auth.clientId);
      if (config.auth?.facilityId) headers.set("x-facility-id", config.auth.facilityId);

      const response = await fetch(`${config.baseUrl}/api/facility-settings/ui-defaults`, {
        headers,
        signal,
      });

      if (!response.ok) return null;

      return response.json();
    },

    // GET /api/v1/admin/care-assessment-scheduler/config/{client_id}/{facility_id}
    getSchedulerConfig: async (
      clientId: string,
      facilityId: string,
      signal?: AbortSignal
    ): Promise<{ recurring_workflows: string[] } | null> => {
      if (!clientId || !facilityId) return null;
      try {
        const response = await apiFetch(
          `/api/v1/admin/care-assessment-scheduler/config/${clientId}/${facilityId}`,
          { signal }
        );
        if (!response.ok) return null;
        return response.json();
      } catch {
        return null;
      }
    },

    // PATCH /api/v1/admin/care-assessment-scheduler/config/{client_id}/{facility_id}
    saveSchedulerConfig: async (
      clientId: string,
      facilityId: string,
      payload: { recurring_workflows: string[] }
    ): Promise<{ recurring_workflows: string[] }> => {
      if (!clientId || !facilityId) {
        throw new Error("Client ID and Facility ID are required");
      }
      const response = await apiFetch(
        `/api/v1/admin/care-assessment-scheduler/config/${clientId}/${facilityId}`,
        {
          method: "PATCH",
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to save scheduler config: ${response.statusText}`);
      }
      return response.json();
    },
  };
}

// Type for the API service instance
export type ApiService = ReturnType<typeof createApiService>;

// Default configuration factory for main app usage
export function createDefaultApiService(): ApiService {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  const authToken = import.meta.env.VITE_AUTH_TOKEN;

  if (!baseUrl) {
    throw new Error("VITE_API_BASE_URL environment variable is required");
  }

  const config: ApiConfig = {
    baseUrl,
    // Only include auth token in development mode
    ...(authToken && import.meta.env.DEV && {
      auth: {
        token: authToken,
      },
    }),
  };

  return createApiService(config);
}

// Export a default instance for main app usage
export const apiService = createDefaultApiService();
